export type RoundingCell = {
  row: number;
  col: number;
  fraction: number;
};

type RoundingState = {
  cells: RoundingCell[];
  cellsByRow: number[][];
  cellsByCol: number[][];
  roundUp: boolean[];
  rowRemaining: number[];
  colRemaining: number[];
};

const groupCellIndexes = (
  cells: RoundingCell[],
  groupCount: number,
  groupOf: (cell: RoundingCell) => number,
) => {
  const groups: number[][] = Array.from({ length: groupCount }, () => []);
  cells.forEach((cell, index) => {
    groups[groupOf(cell)]?.push(index);
  });
  return groups;
};

const roundUpGreedily = (state: RoundingState) => {
  const byFractionDesc = state.cells
    .map((_, index) => index)
    .sort((a, b) => state.cells[b]!.fraction - state.cells[a]!.fraction);

  for (const index of byFractionDesc) {
    const { row, col } = state.cells[index]!;
    if (state.rowRemaining[row]! > 0 && state.colRemaining[col]! > 0) {
      state.roundUp[index] = true;
      state.rowRemaining[row]! -= 1;
      state.colRemaining[col]! -= 1;
    }
  }
};

// Flips cells along the alternating path back from the column that had room.
const applyAugmentingPath = (
  state: RoundingState,
  endColumnNode: number,
  viaCell: (number | undefined)[],
) => {
  const rowCount = state.rowRemaining.length;
  state.colRemaining[endColumnNode - rowCount]! -= 1;

  let node: number | undefined = endColumnNode;
  while (node !== undefined) {
    const cellIndex: number | undefined = viaCell[node];
    if (node < rowCount) {
      if (cellIndex === undefined) {
        state.rowRemaining[node]! -= 1;
        return;
      }
      state.roundUp[cellIndex] = false;
      node = rowCount + state.cells[cellIndex]!.col;
    } else {
      state.roundUp[cellIndex!] = true;
      node = state.cells[cellIndex!]!.row;
    }
  }
};

// BFS over rows -> columns via cells not yet rounded up, and columns -> rows
// via cells already rounded up, to give one more round-up to a row and a
// column that both still have room.
type PathSearch = {
  visited: boolean[];
  viaCell: (number | undefined)[];
  queue: number[];
};

const startPathSearch = (state: RoundingState) => {
  const nodeCount = state.rowRemaining.length + state.colRemaining.length;
  const search: PathSearch = {
    visited: new Array(nodeCount).fill(false),
    viaCell: new Array(nodeCount).fill(undefined),
    queue: [],
  };
  state.rowRemaining.forEach((remaining, row) => {
    if (remaining > 0) {
      search.visited[row] = true;
      search.queue.push(row);
    }
  });
  return search;
};

// Returns the first column node with room reached from `node`.
const expandNode = (state: RoundingState, search: PathSearch, node: number) => {
  const rowCount = state.rowRemaining.length;
  const isRow = node < rowCount;
  const incident = isRow
    ? state.cellsByRow[node]!
    : state.cellsByCol[node - rowCount]!;

  for (const cellIndex of incident) {
    // Rows take cells not yet rounded up; columns take rounded-up ones.
    if (state.roundUp[cellIndex] === isRow) continue;

    const cell = state.cells[cellIndex]!;
    const next = isRow ? rowCount + cell.col : cell.row;
    if (search.visited[next]) continue;

    search.visited[next] = true;
    search.viaCell[next] = cellIndex;
    if (isRow && state.colRemaining[cell.col]! > 0) return next;
    search.queue.push(next);
  }
  return undefined;
};

const augmentOnce = (state: RoundingState) => {
  const search = startPathSearch(state);

  for (let head = 0; head < search.queue.length; head++) {
    const endColumnNode = expandNode(state, search, search.queue[head]!);
    if (endColumnNode !== undefined) {
      applyAugmentingPath(state, endColumnNode, search.viaCell);
      return true;
    }
  }
  return false;
};

// After flooring, each row and column needs `deficit` cells rounded up to hit
// its integer total. A choice always exists when the float matrix matched the
// totals. Larger fractions are taken first, but augmenting paths ignore
// fractions, so the result is feasible rather than globally largest-fraction.
export const chooseCellsToRoundUp = (
  cells: RoundingCell[],
  rowDeficits: number[],
  colDeficits: number[],
) => {
  const state: RoundingState = {
    cells,
    cellsByRow: groupCellIndexes(cells, rowDeficits.length, (c) => c.row),
    cellsByCol: groupCellIndexes(cells, colDeficits.length, (c) => c.col),
    roundUp: cells.map(() => false),
    rowRemaining: [...rowDeficits],
    colRemaining: [...colDeficits],
  };

  roundUpGreedily(state);
  while (augmentOnce(state)) {
    // Each pass adds one round-up, so the loop ends when no pair has room.
  }
  return state.roundUp;
};
